import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import {
    Home,
    Calendar,
    Phone,
    Users,
    LogIn,
    LogOut,
    Menu,
    X,
    Trophy,
    Building2,
    GraduationCap,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

import tflogo from "./assets/tflogo.svg";

// Custom Social Media Icons (Standard SVGs)
const InstagramIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 448 512"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
    </svg>
);

const FacebookIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 512 512"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"></path>
    </svg>
);

const LinkedinIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 448 512"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
    </svg>
);

const YoutubeIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 576 512"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"></path>
    </svg>
);

const XIcon = ({ size = 20 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="none"
    >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 22.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.005 4.066H5.028z" />
    </svg>
);

const WhatsAppIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 448 512"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
    </svg>
);

const DiscordIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 127.14 96.36"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.91-72.15zM42.45 65.69c-6.42 0-11.72-5.9-11.72-13.13s5.17-13.13 11.72-13.13c6.6 0 11.82 5.93 11.72 13.13 0 7.23-5.12 13.13-11.72 13.13zm42.24 0c-6.42 0-11.72-5.9-11.72-13.13s5.17-13.13 11.72-13.13c6.6 0 11.82 5.93 11.72 13.13 0 7.23-5.12 13.13-11.72 13.13z"></path>
    </svg>
);

// CES icon — switched to currentColor so it inherits hover-color transitions
// the same way the other left-sidebar icons do.
const CesIcon = ({ size = 20 }) => (
    <svg
        width={size * (500 / 180)}
        height={size}
        viewBox="0 0 500 180"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M5.445,50.674c-1.723,0.413-2.938,1.955-2.938,3.73v69.016c0,2.476,2.313,4.301,4.721,3.727l45.964-10.987c-14.04-10.808-23.087-27.777-23.087-46.863c0-9.308,2.154-18.114,5.99-25.946L5.445,50.674z M36.094,43.351l62.27-14.887c2.407-0.577,4.724,1.25,4.724,3.726v69.017c0,1.774-1.219,3.317-2.941,3.728l-46.955,11.226c9.972,7.678,22.46,12.243,36.018,12.243c32.644,0,59.104-26.462,59.104-59.106c0-32.64-26.46-59.104-59.104-59.104C65.877,10.192,45.704,23.715,36.094,43.351" />
        <path d="M172.738,69.911c0-34.2,20.325-58.397,56.784-58.397c24.841,0,45.167,12.26,51.458,37.91l-24.843,4.678c-2.581-14.194-11.13-23.391-26.616-23.391c-10.326,0-18.713,2.259-23.875,9.036c-5.163,6.774-7.582,17.26-7.582,30.164s1.29,21.455,6.128,28.392c5.164,7.257,12.906,10.808,25.329,10.808c15.164,0,23.874-8.872,27.098-21.778l25.167,6.615c-8.712,22.262-27.424,34.359-52.265,34.359C193.709,128.307,172.738,107.497,172.738,69.911" />
        <polygon points="294.048,13.451 373.415,13.451 373.415,32.97 318.405,32.97 318.405,59.588 367.443,59.588 367.443,79.105 318.405,79.105 318.405,106.852 373.415,106.852 373.415,126.371 294.048,126.371" />
        <path d="M382.932,88.945l24.843-2.74c0,15.808,10.001,23.389,24.521,23.389c12.583,0,22.26-4.195,22.26-15.488c0-10.966-11.451-11.449-28.549-15.161c-22.59-4.839-40.009-12.746-40.009-34.359c0-23.068,22.26-33.071,44.041-33.071c24.517,0,43.068,9.681,45.811,36.136l-24.68,3.871c-0.646-15-8.227-21.455-21.296-21.455c-11.613,0-19.193,4.519-19.193,13.391c0,8.387,9.031,11.612,25.164,14.516c24.518,4.358,43.554,11.132,43.554,35.492c0,27.099-24.197,34.845-47.748,34.845C405.678,128.309,383.736,116.209,382.932,88.945" />
    </svg>
);

// ---------------------------------------------------------------------------
// SVG Background Components
// ---------------------------------------------------------------------------

const NavBg4Fields = ({ color }) => (
    <svg
        width="1065"
        height="63"
        viewBox="0 0 1065 63"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
    >
        <path
            d="M38.5 38L13 14L38.5 0H1025.5L1052 14.5L1025.5 39V45.5L1016 54H899L895 50H870H841L837 54H742L737 50H682L678 54H636.5L633 50H578L573 54H546H491.5L487.5 50L432.5 49L428.5 54H387.5L384 50H328L323.5 53H270L230.5 54L226 50H170.5L167 54H48.5L38.5 46.5V38Z"
            fill="black"
            fillOpacity="0.32"
        />
        <path
            d="M40.0912 39.1625L17.6899 19.1895V33.5035L25.0346 39.1625V49.149L38.9895 61.4658H113.171L120.515 53.4765H48.1703L40.0912 45.1544V39.1625Z"
            fill="url(#paint0_linear)"
        />
        <path
            d="M206.448 59.8013L195.798 49.8148H225.544L229.584 53.8094H256.759L248.68 59.8013H206.448Z"
            fill="url(#paint1_linear)"
        />
        <path
            d="M40.0912 39.1625L17.6899 19.1895V33.5035L25.0346 39.1625V49.149L38.9895 61.4658H113.171L120.515 53.4765H48.1703L40.0912 45.1544V39.1625Z"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M206.448 59.8013L195.798 49.8148H225.544L229.584 53.8094H256.759L248.68 59.8013H206.448Z"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M0 14H12.8532L39.3546 39.4967V45.8215L47.801 53.8107H166.418L170.09 49.4832H225.542L229.951 53.1411H270.347"
            stroke={color}
            strokeWidth="2"
        />
        <path
            d="M468.602 59.8195L457.953 49.833H487.699L491.738 53.8276H518.913L510.834 59.8195H468.602Z"
            fill="url(#paint2_linear)"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M407.795 53.8265H428.572L432.244 49.499H487.697L492.103 53.8265H532.499"
            stroke={color}
            strokeWidth="2"
        />
        <path
            d="M346.986 59.5117L357.636 49.8223H327.89L323.85 53.698H296.675L304.754 59.5117H346.986Z"
            fill="url(#paint3_linear)"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M407.793 53.6978H387.016L383.344 49.499H327.892L323.485 53.6978H283.089"
            stroke={color}
            strokeWidth="2"
        />
        <path
            d="M718.014 59.8195L707.364 49.833H737.11L741.149 53.8276H768.325L760.245 59.8195H718.014Z"
            fill="url(#paint4_linear)"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M657.207 53.8265H677.983L681.656 49.499H737.108L741.515 53.8265H781.91"
            stroke={color}
            strokeWidth="2"
        />
        <path
            d="M596.398 59.5117L607.048 49.8223H577.302L573.262 53.698H546.087L554.166 59.5117H596.398Z"
            fill="url(#paint5_linear)"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M657.205 53.6978H636.428L632.755 49.499H577.303L572.897 53.6978H532.501"
            stroke={color}
            strokeWidth="2"
        />
        <path
            d="M1024.91 39.1625L1047.31 19.1895V33.5035L1039.97 39.1625V49.149L1026.01 61.4658H951.829L944.485 53.4765H1016.83L1024.91 45.1544V39.1625Z"
            fill="url(#paint6_linear)"
        />
        <path
            d="M858.552 59.8013L869.202 49.8148H839.456L835.416 53.8094H808.241L816.32 59.8013H858.552Z"
            fill="url(#paint7_linear)"
        />
        <path
            d="M1024.91 39.1625L1047.31 19.1895V33.5035L1039.97 39.1625V49.149L1026.01 61.4658H951.829L944.485 53.4765H1016.83L1024.91 45.1544V39.1625Z"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M858.552 59.8013L869.202 49.8148H839.456L835.416 53.8094H808.241L816.32 59.8013H858.552Z"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M1065 14H1052.15L1025.65 39.4967V45.8215L1017.2 53.8107H898.582L894.91 49.4832H839.458L835.051 53.8107H794.656"
            stroke={color}
            strokeWidth="2"
        />
        <defs>
            <linearGradient
                id="paint0_linear"
                x1="139.905"
                y1="-59.0452"
                x2="136.228"
                y2="87.6134"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint1_linear"
                x1="139.905"
                y1="-59.0452"
                x2="136.228"
                y2="87.6134"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint2_linear"
                x1="489.117"
                y1="31.3524"
                x2="488.312"
                y2="65.9992"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint3_linear"
                x1="326.472"
                y1="31.8915"
                x2="327.229"
                y2="65.5086"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint4_linear"
                x1="738.528"
                y1="31.3524"
                x2="737.723"
                y2="65.9992"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint5_linear"
                x1="575.884"
                y1="31.8915"
                x2="576.641"
                y2="65.5086"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint6_linear"
                x1="925.095"
                y1="-59.0452"
                x2="928.772"
                y2="87.6134"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint7_linear"
                x1="925.095"
                y1="-59.0452"
                x2="928.772"
                y2="87.6134"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
        </defs>
    </svg>
);

const NavBg3Fields = ({ color }) => (
    <svg
        width="816"
        height="63"
        viewBox="0 0 816 63"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
    >
        <path
            d="M38.5 38 L13 14 L38.5 0 H776 L802.5 14.5 L776 39 V45.5 L766.5 54 H649.5 L645.5 50 H620.5 H591.5 L587.5 54 H492.5 L487.5 50 H432.5 L428.5 54 H387.5 L384 50 H328 L323.5 53 H270 L230.5 54 L226 50 H170.5 L167 54 H48.5 L38.5 46.5 V38 Z"
            fill="black"
            fillOpacity="0.32"
        />
        <path
            d="M40.0912 39.1625L17.6899 19.1895V33.5035L25.0346 39.1625V49.149L38.9895 61.4658H113.171L120.515 53.4765H48.1703L40.0912 45.1544V39.1625Z"
            fill="url(#paint0_3f)"
        />
        <path
            d="M206.448 59.8013L195.798 49.8148H225.544L229.584 53.8094H256.759L248.68 59.8013H206.448Z"
            fill="url(#paint1_3f)"
        />
        <path
            d="M40.0912 39.1625L17.6899 19.1895V33.5035L25.0346 39.1625V49.149L38.9895 61.4658H113.171L120.515 53.4765H48.1703L40.0912 45.1544V39.1625Z"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M206.448 59.8013L195.798 49.8148H225.544L229.584 53.8094H256.759L248.68 59.8013H206.448Z"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M0 14H12.8532L39.3546 39.4967V45.8215L47.801 53.8107H166.418L170.09 49.4832H225.542L229.951 53.1411H270.347"
            stroke={color}
            strokeWidth="2"
        />
        <path
            d="M346.986 59.5117L357.636 49.8223H327.89L323.85 53.698H296.675L304.754 59.5117H346.986Z"
            fill="url(#paint3_3f)"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M407.793 53.6978H387.016L383.344 49.499H327.892L323.485 53.6978H283.089"
            stroke={color}
            strokeWidth="2"
        />
        <path
            d="M468.514 59.8195L457.864 49.833H487.61L491.649 53.8276H518.825L510.745 59.8195H468.514Z"
            fill="url(#paint4_3f)"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M407.707 53.8265H428.483L432.156 49.499H487.608L492.015 53.8265H532.41"
            stroke={color}
            strokeWidth="2"
        />
        <path
            d="M775.41 39.1625L797.81 19.1895V33.5035L790.47 39.1625V49.149L776.51 61.4658H702.329L694.985 53.4765H767.33L775.41 45.1544V39.1625Z"
            fill="url(#paint6_3f)"
        />
        <path
            d="M609.052 59.8013L619.702 49.8148H589.956L585.916 53.8094H558.741L566.82 59.8013H609.052Z"
            fill="url(#paint6_3f)"
        />
        <path
            d="M775.41 39.1625L797.81 19.1895V33.5035L790.47 39.1625V49.149L776.51 61.4658H702.329L694.985 53.4765H767.33L775.41 45.1544V39.1625Z"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M609.052 59.8013L619.702 49.8148H589.956L585.916 53.8094H558.741L566.82 59.8013H609.052Z"
            stroke={color}
            strokeWidth="3"
        />
        <path
            d="M815.5 14H802.65L776.15 39.4967V45.8215L767.7 53.8107H649.082L645.41 49.4832H589.958L585.551 53.8107H545.156"
            stroke={color}
            strokeWidth="2"
        />
        <defs>
            <linearGradient
                id="paint0_3f"
                x1="139.905"
                y1="-59.0452"
                x2="136.228"
                y2="87.6134"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint1_3f"
                x1="139.905"
                y1="-59.0452"
                x2="136.228"
                y2="87.6134"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint3_3f"
                x1="326.472"
                y1="31.8915"
                x2="327.229"
                y2="65.5086"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint4_3f"
                x1="489.028"
                y1="31.3524"
                x2="488.223"
                y2="65.9992"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
            <linearGradient
                id="paint6_3f"
                x1="675.595"
                y1="-59.0452"
                x2="679.272"
                y2="87.6134"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={color} stopOpacity="0.4" />
                <stop offset="1" stopColor={color} />
            </linearGradient>
        </defs>
    </svg>
);

// ---------------------------------------------------------------------------
// Central nav config
// ---------------------------------------------------------------------------
const topNavItems = [
    { label: "Accommodation", href: "/accommodation" },
    { label: "Workshops", href: "/workshops" },
    { label: "Competitions", href: "/competitions" },
    { label: "Ignite", href: "/ignite" },
];

const eventsItems = [
    { label: "Lectures", href: "/lectures" },
    { label: "Exhibitions", href: "/exhibitions" },
    { label: "Social Initiative", href: "/social-initiative" },
    { label: "TWMUN", href: "/twmun" },
];

const mobileEventsItems = [
    { label: "Lectures", href: "/lectures" },
    { label: "Exhibitions", href: "/exhibitions" },
    { label: "Social Initiative", href: "/social-initiative" },
    { label: "Techfest World MUN", href: "/twmun" },
    { label: "Ignite", href: "/ignite" },
];

const aboutItems = [
    { label: "History", href: "/history" },
    { label: "Media", href: "/media" },
    { label: "Gallery", href: "/gallery" },
    { label: "Recognition", href: "/recognition" },
    { label: "Legals", href: "/legals" },
];

const contactItem = { label: "Contact Us", href: "/contactus" };

const socialLinks = [
    {
        Icon: InstagramIcon,
        href: "https://www.instagram.com/techfest_iitbombay/?hl=en",
        label: "Instagram",
        color: "#E1306C",
    },
    {
        Icon: XIcon,
        href: "https://x.com/Techfest_IITB?lang=en",
        label: "X",
        color: "#1DA1F2",
    },
    {
        Icon: LinkedinIcon,
        href: "https://in.linkedin.com/company/techfest",
        label: "LinkedIn",
        color: "#0A66C2",
    },
    {
        Icon: FacebookIcon,
        href: "https://www.facebook.com/iitbombaytechfest/",
        label: "Facebook",
        color: "#1877F2",
    },
    {
        Icon: YoutubeIcon,
        href: "https://www.youtube.com/user/techfestiitbombay",
        label: "YouTube",
        color: "#FF0000",
    },
    {
        Icon: WhatsAppIcon,
        href: "https://whatsapp.com/channel/0029VaqxA9IAjPXM1339Pv41",
        label: "WhatsApp",
        color: "#25D366",
    },
    {
        Icon: DiscordIcon,
        href: "https://discord.gg/AGkCVSctQ",
        label: "Discord",
        color: "#5865F2",
    },
];

const bottomNavItems = [
    { label: "Home", href: "/", Icon: Home },
    {
        label: "Events",
        Icon: Calendar,
        type: "popout",
        items: mobileEventsItems,
    },
    { label: "Competitions", href: "/competitions", Icon: Trophy },
    { label: "Accommodation", href: "/accommodation", Icon: Building2 },
    { label: "Workshops", href: "/workshops", Icon: GraduationCap },
];

const PopoutBox = ({
    isOpen,
    onClose,
    gsapLoaded,
    variant = "inline",
    inlineItems,
    fullscreenData,
    positionClass = "",
}) => {
    const [isMounted, setIsMounted] = useState(isOpen);
    const containerRef = useRef(null);
    const glassRef = useRef(null);
    const contentRef = useRef(null);
    const pixelsRef = useRef([]);

    const gridCols = variant === "fullscreen" ? 20 : 10;
    const pixelCount = gridCols * gridCols;

    useEffect(() => {
        if (isOpen) setIsMounted(true);
    }, [isOpen]);

    useEffect(() => {
        if (!isMounted || !gsap) return;

        const pixels = pixelsRef.current.filter(Boolean);

        gsap.killTweensOf(pixels);
        if (glassRef.current) gsap.killTweensOf(glassRef.current);
        if (contentRef.current) gsap.killTweensOf(contentRef.current.children);

        if (isOpen) {
            const tl = gsap.timeline({
                onComplete: () => {
                    gsap.set(pixels, { opacity: 0 });
                },
            });

            tl.set(glassRef.current, { visibility: "visible" }, 0).fromTo(
                glassRef.current,
                { opacity: 0, scaleX: 0.1, transformOrigin: "left center" },
                {
                    opacity: 1,
                    scaleX: 1,
                    duration: variant === "fullscreen" ? 0.55 : 0.45,
                    ease: "power2.inOut",
                },
                0,
            );

            pixels.forEach((pixel, i) => {
                const col = i % gridCols;
                const baseDelay =
                    (col / gridCols) * (variant === "fullscreen" ? 0.45 : 0.35);
                const randomDelay = Math.random() * 0.1;
                const delay = baseDelay + randomDelay;
                const flashDuration = 0.02 + Math.random() * 0.05;

                tl.set(pixel, { opacity: Math.random() * 0.6 + 0.4 }, delay);
                tl.set(pixel, { opacity: 0 }, delay + flashDuration);
            });

            tl.fromTo(
                contentRef.current.children,
                { opacity: 0, x: -10 },
                {
                    opacity: 1,
                    x: 0,
                    duration: 0.25,
                    stagger: 0.04,
                    ease: "power2.out",
                },
                0.15,
            );
        } else {
            const revTl = gsap.timeline({
                onComplete: () => {
                    gsap.set(pixels, { opacity: 0 });
                    setIsMounted(false);
                },
            });

            revTl
                .to(
                    contentRef.current.children,
                    { opacity: 0, x: -10, duration: 0.2, stagger: 0.03 },
                    0,
                )
                .to(
                    glassRef.current,
                    {
                        opacity: 0,
                        scaleX: 0.2,
                        duration: 0.4,
                        ease: "power2.inOut",
                    },
                    0.05,
                )
                .set(glassRef.current, { visibility: "hidden" });

            pixels.forEach((pixel, i) => {
                const col = i % gridCols;
                const revCol = gridCols - 1 - col;
                const baseDelay =
                    (revCol / gridCols) *
                    (variant === "fullscreen" ? 0.4 : 0.3);
                const randomDelay = Math.random() * 0.1;
                const delay = baseDelay + randomDelay;
                const flashDuration = 0.02 + Math.random() * 0.05;

                revTl.set(pixel, { opacity: Math.random() * 0.6 + 0.4 }, delay);
                revTl.set(pixel, { opacity: 0 }, delay + flashDuration);
            });
        }
    }, [isOpen, isMounted, gsapLoaded, variant, gridCols]);

    if (!isMounted) return null;

    return (
        <div
            className={`popout-container ${variant === "fullscreen" ? "fullscreen" : ""} ${positionClass}`}
            ref={containerRef}
        >
            <div
                className={`pixels-layer ${variant === "fullscreen" ? "fullscreen-pixels" : ""}`}
                style={{
                    gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                    gridTemplateRows: `repeat(${gridCols}, 1fr)`,
                }}
            >
                {Array.from({ length: pixelCount }).map((_, i) => (
                    <div
                        key={i}
                        className="pixel"
                        ref={(el) => (pixelsRef.current[i] = el)}
                    />
                ))}
            </div>

            <div
                className={`popout-glass ${variant === "fullscreen" ? "fullscreen-glass" : ""}`}
                ref={glassRef}
                style={{ visibility: "hidden" }}
            >
                {variant === "fullscreen" ? (
                    <div className="fullscreen-content" ref={contentRef}>
                        <div className="fullscreen-col fullscreen-col-left">
                            {fullscreenData.about.map((item) => (
                                <a
                                    key={item.label}
                                    href={item.href}
                                    className="fullscreen-link"
                                    onClick={onClose}
                                >
                                    {item.label}
                                </a>
                            ))}
                            <a
                                href={fullscreenData.contact.href}
                                className="fullscreen-contact-btn"
                                onClick={onClose}
                            >
                                <Phone size={16} />
                                {fullscreenData.contact.label}
                            </a>
                        </div>
                        <div className="fullscreen-col fullscreen-col-right">
                            {fullscreenData.social.map(
                                ({ Icon, href, label, color }) => (
                                    <a
                                        key={label}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="fullscreen-social-link"
                                        aria-label={label}
                                        style={{ "--brand": color }}
                                    >
                                        <Icon size={26} />
                                    </a>
                                ),
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="popout-content" ref={contentRef}>
                        {inlineItems.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                className="popout-item"
                                onClick={onClose}
                            >
                                {item.label}
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, loginWithGoogle, authFetch, logout } = useAuth();
    const [tfUser, setTfUser] = useState(null);
    const [activeMenu, setActiveMenu] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [gsapLoaded, setGsapLoaded] = useState(true);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [hoveredNavIndex, setHoveredNavIndex] = useState(null);

    const isProfilePage = location.pathname === "/profile";

    const getNavColor = (pathname) => {
        return "#B5B5B5";
    };

    const currentNavColor = getNavColor(location.pathname);

    const isFourFields = topNavItems.length === 4;
    const navWidth = isFourFields ? 1065 : 816;
    const navAspect = isFourFields ? "1065 / 63" : "816 / 63";

    useEffect(() => {
        if (currentUser && !tfUser) {
            authFetch("https://techfest.org/api/google-signin/", {
                method: "POST",
            })
                .then((res) => (res.ok ? res.json() : null))
                .then((data) => {
                    if (data) setTfUser(data);
                })
                .catch((err) =>
                    console.error("Failed to load TF profile", err),
                );
        }
    }, [currentUser, tfUser, authFetch]);

    const toggleMenu = (menu) => {
        setActiveMenu((prev) => (prev === menu ? null : menu));
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                !e.target.closest(".sidebar-item") &&
                !e.target.closest(".bottom-nav-item") &&
                !e.target.closest(".popout-container")
            ) {
                setActiveMenu(null);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (!gsapLoaded || !gsap) return;
        const logoTl = gsap.timeline({ repeat: -1, repeatDelay: 3 });
        logoTl.fromTo(
            ".logo-text",
            { backgroundPosition: "200% center" },
            {
                backgroundPosition: "-200% center",
                duration: 1.5,
                ease: "power2.inOut",
            },
        );
    }, [gsapLoaded]);

    return (
        <div className="app-wrapper">
            <style
                dangerouslySetInnerHTML={{
                    __html: `
                * { box-sizing: border-box; margin: 0; padding: 0; }
                
                .app-wrapper {
                    position: fixed;
                    top: 0; left: 0;
                    width: 100vw; height: 100vh;
                    pointer-events: none;
                    z-index: 100;
                }

                .logo-container {
                    position: absolute; top: 30px; left: 30px;
                    display: flex; align-items: center; justify-content: center;
                    cursor: pointer; z-index: 20; pointer-events: auto;
                }
                .logo-img { height: 50px; vertical-align: middle; transition: height 0.2s ease; }
                .logo-text {
                    font-weight: 900; font-style: italic; 
                    font-size: 32px; letter-spacing: -2px; z-index: 2;
                    background: linear-gradient(120deg, #ffffff 40%, #88c0d0 50%, #ffffff 60%);
                    background-size: 300% 100%;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    text-shadow: 0 0 15px rgba(255,255,255,0.1);
                }

                .top-nav {
                    position: absolute;
                    top: 30px; 
                    left: 50%; 
                    transform: translateX(-50%);
                    display: flex; 
                    align-items: stretch; 
                    justify-content: center;
                    background: transparent;
                    padding: 0 clamp(8px, 2.5vw, 36px); 
                    border: none; 
                    box-shadow: none; 
                    backdrop-filter: none;
                    z-index: 10; 
                    pointer-events: auto;
                    --nav-gap: clamp(14px, 2.6vw, 40px);
                    gap: var(--nav-gap);
                    width: 48%;
                    max-width: var(--nav-max-width, 1065px);
                    aspect-ratio: var(--nav-aspect, 1065 / 63);
                    height: auto;
                }
                .top-nav-bg-wrapper {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 120%;
                    height: 120%;
                    z-index: -1;
                }
                .top-nav-bg-wrapper svg { width: 100%; height: 100%; display: block; }
                
                .top-nav-item {
                    color: #fff; text-decoration: none; 
                    font-size: clamp(11px, 1.35vw, 15px); font-weight: 700;
                    letter-spacing: clamp(0.5px, 0.1vw, 1.5px); cursor: pointer; transition: font-weight 0.3s;
                    text-transform: uppercase; flex: 0 1 auto;
                    white-space: nowrap; text-align: center;
                    display: flex; justify-content: center; align-items: flex-start;
                    padding-top: 8px;
                    min-width: 0;
                }
                .top-nav-item.active { font-weight: 900; }

                .top-nav-label {
                    position: relative;
                    display: inline-block;
                    padding-bottom: 6px;
                }
                .top-nav-item:hover {
                    color: #fff; 
                    text-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
                }

                .early-bird-badge {
                    position: absolute;
                    top: -9px;
                    left: -2px;
                    font-size: clamp(7px, 0.72vw, 9px);
                    font-weight: 900;
                    color: #FFE600;
                    text-shadow: 
                        -1px -1px 0 #000,
                        1px -1px 0 #000,
                        -1px 1px 0 #000,
                        1px 1px 0 #000,
                        0 0 4px #000,
                        0 0 8px rgba(255, 230, 0, 0.7);
                    letter-spacing: 0.6px;
                    white-space: nowrap;
                    text-transform: uppercase;
                    pointer-events: none;
                    line-height: 1;
                    transform-origin: bottom left;
                    animation: earlyBirdPulse 1.2s ease-in-out infinite alternate;
                    z-index: 5;
                }
                @keyframes earlyBirdPulse {
                    0% {
                        transform: scale(0.88);
                    }
                    100% {
                        transform: scale(1.15);
                    }
                }

                .nav-divider {
                    width: 2px;
                    height: 26px;
                    align-self: center;
                    background-color: var(--divider-color);
                    transform-origin: bottom center;
                    transform: scaleY(0);
                    opacity: 0;
                    transition:
                        transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
                        opacity 0.25s ease;
                    z-index: 20;
                    box-shadow: 0 0 0 rgba(0,0,0,0);
                    pointer-events: none;
                }
                .nav-divider.active {
                    transform: scaleY(1);
                    opacity: 1;
                    box-shadow: 0 0 10px var(--divider-color), 0 0 18px var(--divider-color);
                }

                .sign-in-btn {
                    position: absolute; top: 30px; right: 30px;
                    background: rgba(15, 25, 45, 0.6); 
                    backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(150, 200, 255, 0.2);
                    color: white; padding: 12px 24px; border-radius: 8px;
                    cursor: pointer; font-weight: 600; display: flex; align-items: center; gap: 10px;
                    transition: 0.3s; letter-spacing: 1px; z-index: 10;
                    text-transform: uppercase; font-size: 13px; pointer-events: auto;
                }
                .sign-in-btn:hover { background: rgba(40, 70, 120, 0.8); box-shadow: 0 0 15px rgba(150, 200, 255, 0.3); }
                .sign-in-btn.sign-out { border-color: rgba(239, 68, 68, 0.35); background: rgba(30, 15, 25, 0.65); }
                .sign-in-btn.sign-out:hover { background: rgba(180, 40, 40, 0.75); box-shadow: 0 0 15px rgba(239, 68, 68, 0.4); border-color: rgba(239, 68, 68, 0.6); }

                .logout-modal-backdrop {
                    position: fixed; inset: 0; background: rgba(2, 6, 23, 0.75);
                    backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1000; pointer-events: auto; padding: 20px;
                }
                .logout-modal-card {
                    background: rgba(15, 25, 45, 0.85); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(150, 200, 255, 0.25); border-top: 1px solid rgba(255, 255, 255, 0.4);
                    border-radius: 14px; padding: 28px 24px; max-width: 380px; width: 100%; text-align: center;
                    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6), inset 0 0 20px rgba(255, 255, 255, 0.05);
                    animation: modalPop 0.25s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes modalPop { from { transform: scale(0.92); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                .logout-modal-icon {
                    width: 54px; height: 54px; margin: 0 auto 16px; border-radius: 50%;
                    background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.4);
                    color: #ef4444; display: flex; align-items: center; justify-content: center;
                }
                .logout-modal-title { font-size: 1.3rem; font-weight: 700; color: #ffffff; margin-bottom: 8px; }
                .logout-modal-message { font-size: 0.95rem; color: rgba(255, 255, 255, 0.7); margin-bottom: 24px; line-height: 1.5; }
                .logout-modal-actions { display: flex; gap: 12px; justify-content: center; }
                .logout-modal-btn {
                    flex: 1; padding: 11px 18px; border-radius: 8px; font-size: 13px; font-weight: 600; letter-spacing: 0.5px;
                    cursor: pointer; transition: all 0.2s ease; border: none; text-transform: uppercase;
                }
                .logout-modal-btn.cancel { background: rgba(255, 255, 255, 0.08); color: #e2e8f0; border: 1px solid rgba(255, 255, 255, 0.15); }
                .logout-modal-btn.cancel:hover { background: rgba(255, 255, 255, 0.15); color: #ffffff; }
                .logout-modal-btn.confirm { background: rgba(239, 68, 68, 0.85); color: #ffffff; box-shadow: 0 0 15px rgba(239, 68, 68, 0.4); }
                .logout-modal-btn.confirm:hover { background: #dc2626; box-shadow: 0 0 20px rgba(239, 68, 68, 0.6); transform: translateY(-1px); }

                .sidebar {
                    position: absolute; top: 50%; transform: translateY(-50%);
                    display: flex; flex-direction: column; gap: 0px;
                    background: rgba(10, 15, 30, 0.6); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(150, 200, 255, 0.15); padding: 15px 5px; border-radius: 12px;
                    z-index: 10; pointer-events: auto; box-shadow: 0 10px 40px rgba(0,0,0,0.6);
                }
                .sidebar.left { left: 20px; }
                .sidebar.right { right: 30px; padding: 18px 12px; gap: 6px; }

                .sidebar.left .sidebar-item { color: #ffffff; }
                .sidebar-item {
                    display: flex; flex-direction: column; align-items: center;
                    color: #a0aec0; cursor: pointer; transition: 0.3s;
                    font-size: 10px; gap: 6px; text-transform: capitalize; 
                    letter-spacing: 0.5px; padding: 12px 5px; border-radius: 8px;
                    position: relative; text-decoration: none; background: none; border: none;
                }
                .sidebar-item:hover, .sidebar-item.active { color: #6fc4f2; }
                .sidebar.left .sidebar-item:hover { color: #6fc4f2; text-shadow: 0 0 10px rgba(255,255,255,0.4); }
                .sidebar-item svg { stroke-width: 1.5; }

                .sidebar.right .sidebar-item { color: #ffffff; padding: 10px; transition: color 0.3s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
                .sidebar.right .sidebar-item:hover { color: var(--brand, #88c0d0); transform: rotate(12deg) scale(1.15); text-shadow: 0 0 10px rgba(255,255,255,0.4); }

                .popout-container { position: absolute; left: 75px; top: 50%; transform: translateY(-50%); width: max-content; min-width: 140px; z-index: 5; pointer-events: none; }
                .pixels-layer { position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: grid; z-index: 2; }
                .pixel { background: rgba(255,255,255,0.9); opacity: 0; box-shadow: 0 0 5px white; }
                
                .popout-glass {
                    position: relative; width: 100%; height: 100%;
                    background: rgba(15, 25, 45, 0.85); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(150, 200, 255, 0.3); border-radius: 12px;
                    padding: 12px 16px; box-shadow: 0 15px 40px rgba(0,0,0,0.7), inset 0 0 20px rgba(255,255,255,0.05); pointer-events: auto;
                }
                .popout-content { display: flex; flex-direction: column; gap: 6px; }
                .popout-item {
                    color: #e2e8f0; font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; cursor: pointer; transition: 0.2s; 
                    padding: 6px 4px; border-bottom: 1px solid rgba(255,255,255,0.1); font-weight: 500; text-decoration: none; display: block;
                }
                .popout-item:hover { color: #63b3ed; padding-left: 10px; border-color: #63b3ed; background: linear-gradient(90deg, rgba(99,179,237,0.1) 0%, transparent 100%); }

                .hamburger-btn {
                    display: none; position: fixed; top: 18px; left: 18px;
                    background: rgba(15, 25, 45, 0.65); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(150, 200, 255, 0.2); color: white; border-radius: 8px;
                    width: 42px; height: 42px; align-items: center; justify-content: center; cursor: pointer; z-index: 250; pointer-events: auto;
                }

                .popout-container.fullscreen { position: fixed; inset: 0; left: 0; top: 0; width: 100vw; height: 100vh; transform: none; min-width: 0; z-index: 200; }
                .pixels-layer.fullscreen-pixels { position: fixed; inset: 0; }
                .popout-glass.fullscreen-glass {
                    width: 100vw; height: 100vh; background: rgba(6, 10, 20, 0.85); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
                    border: none; border-radius: 0; padding: 90px 28px 40px; box-shadow: none;
                }
                .fullscreen-content { display: flex; flex-direction: column; justify-content: space-between; height: 100%; gap: 48px; }
                .fullscreen-close {
                    position: absolute; top: 18px; right: 18px; background: rgba(255,255,255,0.06); border: 1px solid rgba(150, 200, 255, 0.2);
                    color: white; border-radius: 8px; width: 42px; height: 42px; display: flex; align-items: center; justify-content: center; cursor: pointer;
                }
                .fullscreen-col { display: flex; flex-direction: column; }
                .fullscreen-col-left { gap: 22px; }
                .fullscreen-link { color: #ffffff; text-decoration: none; font-size: 26px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; }
                .fullscreen-link:active { color: #6fc4f2; }
                .fullscreen-contact-btn {
                    margin-top: 12px; display: inline-flex; align-items: center; gap: 10px; width: fit-content; color: #ffffff; text-decoration: none;
                    border: 1px solid rgba(150, 200, 255, 0.4); border-radius: 8px; padding: 12px 22px; font-size: 13px; font-weight: 600; letter-spacing: 1px;
                    text-transform: uppercase; background: rgba(111, 196, 242, 0.08);
                }
                .fullscreen-col-right { flex-direction: row; flex-wrap: wrap; gap: 22px; align-items: center; }
                .fullscreen-social-link { color: #ffffff; display: flex; align-items: center; justify-content: center; transition: color 0.3s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
                .fullscreen-social-link:hover, .fullscreen-social-link:active { color: var(--brand, #88c0d0); transform: rotate(12deg) scale(1.15); }

                .bottom-nav {
                    display: none; position: fixed; bottom: 0; left: 0; width: 100%;
                    background: rgba(10, 15, 30, 0.85); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
                    border-top: 1px solid rgba(150, 200, 255, 0.15); padding: 8px 4px calc(8px + env(safe-area-inset-bottom));
                    z-index: 150; pointer-events: auto; justify-content: space-around; align-items: center;
                }
                .bottom-nav-item { display: flex; flex-direction: column; align-items: center; gap: 4px; color: #a0aec0; text-decoration: none; font-size: 7px; letter-spacing: 0.3px; text-transform: uppercase; font-weight: 500; flex: 1; text-align: center; cursor: pointer; position: relative; }
                .bottom-nav-item:active { color: #6fc4f2; }
                .bottom-nav-item svg { stroke-width: 1.5; }
                .popout-container.bottom-popout { left: 50%; top: auto; bottom: 100%; transform: translateX(-50%); margin-bottom: 14px; z-index: 160; }

                .mobile-ces-btn {
                    display: none;
                    position: absolute;
                    background: rgba(15, 25, 45, 0.6);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(150, 200, 255, 0.2);
                    color: white;
                    cursor: pointer;
                    align-items: center;
                    justify-content: center;
                    transition: 0.3s;
                    z-index: 10;
                    pointer-events: auto;
                    text-decoration: none;
                }
                .mobile-ces-btn:hover, .mobile-ces-btn:active {
                    background: rgba(40, 70, 120, 0.8);
                    box-shadow: 0 0 15px rgba(150, 200, 255, 0.3);
                    color: #6fc4f2;
                }

                @media (max-width: 1024px) {
                    .logo-img { height: 42px; }
                    .top-nav { width: 50%; top: 25px; --nav-gap: clamp(10px, 2vw, 26px); padding: 0 clamp(6px, 2vw, 24px); }
                    .top-nav-item { font-size: clamp(9px, 1.3vw, 13px); letter-spacing: clamp(0.3px, 0.08vw, 1.2px); }
                    .sign-in-btn { padding: 10px 18px; font-size: 12px; }
                    .sidebar.left, .sidebar.right { padding: 10px 4px; }
                    .sidebar.right { padding: 14px 9px; gap: 4px; }
                    .sidebar-item { padding: 10px 4px; font-size: 9px; gap: 5px; }
                    .sidebar-item svg { width: 19px; height: 19px; }
                    .sidebar.right .sidebar-item svg { width: 22px; height: 22px; }
                    .popout-container { left: 66px; }
                }

                @media (max-width: 768px) {
                    .logo-img { height: 34px; }
                    .logo-container { top: 20px; left: 20px; }
                    .top-nav { top: 20px; width: 70%; --nav-gap: clamp(8px, 1.5vw, 18px); padding: 0 clamp(4px, 1.5vw, 16px); }
                    .top-nav-item { font-size: clamp(8px, 1.6vw, 11px); letter-spacing: 0.5px; }
                    .sign-in-btn { top: 20px; right: 20px; padding: 8px 14px; font-size: 11px; gap: 6px; }
                    .mobile-ces-btn {
                        display: flex;
                        top: 60px;
                        right: 20px;
                        padding: 6px 12px;
                        border-radius: 8px;
                    }
                    .sidebar { padding: 8px 3px; border-radius: 10px; }
                    .sidebar.right { padding: 10px 7px; gap: 3px; }
                    .sidebar.left { left: 12px; }
                    .sidebar.right { right: 14px; }
                    .sidebar-item { padding: 8px 3px; font-size: 8px; gap: 4px; }
                    .sidebar-item svg { width: 16px; height: 16px; }
                    .sidebar.right .sidebar-item svg { width: 19px; height: 19px; }
                    .popout-container { left: 52px; }
                    .popout-item { font-size: 10px; }
                }

                @media (max-width: 480px) {
                    .top-nav { display: none; }
                    .sidebar.left, .sidebar.right { display: none; }
                    .hamburger-btn { display: flex; }
                    .bottom-nav { display: flex; }
                    .logo-container { top: 16px; left: 68px; }
                    .logo-img { height: 28px; }
                    .sign-in-btn { top: 16px; right: 16px; padding: 8px 12px; font-size: 10px; gap: 5px; border-radius: 6px; }
                    .mobile-ces-btn {
                        display: flex;
                        top: 54px;
                        right: 16px;
                        padding: 5px 10px;
                        border-radius: 6px;
                    }
                    .fullscreen-glass { padding: 84px 22px 34px; }
                    .fullscreen-link { font-size: 22px; }
                    .fullscreen-contact-btn { font-size: 12px; padding: 10px 18px; }
                    .fullscreen-social-link svg { width: 24px; height: 24px; }
                }
            `,
                }}
            />

            <button
                className="hamburger-btn"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

            <PopoutBox
                variant="fullscreen"
                isOpen={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                gsapLoaded={gsapLoaded}
                fullscreenData={{
                    about: aboutItems,
                    contact: contactItem,
                    social: socialLinks,
                }}
            />

            <a href="/" className="logo-container">
                <div className="logo-text">
                    <img src={tflogo} alt="TF Logo" className="logo-img" />
                </div>
            </a>

            <div
                className="top-nav"
                style={{
                    "--nav-max-width": `${navWidth}px`,
                    "--nav-aspect": navAspect,
                }}
                onMouseLeave={() => setHoveredNavIndex(null)}
            >
                <div className="top-nav-bg-wrapper">
                    {isFourFields ? (
                        <NavBg4Fields color={currentNavColor} />
                    ) : (
                        <NavBg3Fields color={currentNavColor} />
                    )}
                </div>

                {topNavItems.map((item, index) => {
                    const dividerBeforeActive =
                        index > 0 &&
                        (hoveredNavIndex === index ||
                            hoveredNavIndex === index - 1);

                    return (
                        <React.Fragment key={item.label}>
                            {index > 0 && (
                                <div
                                    className={`nav-divider ${dividerBeforeActive ? "active" : ""}`}
                                    style={{
                                        "--divider-color": currentNavColor,
                                    }}
                                />
                            )}
                            <a
                                href={item.href}
                                className={`top-nav-item ${item.active ? "active" : ""}`}
                                onMouseEnter={() => setHoveredNavIndex(index)}
                            >
                                <span className="top-nav-label">
                                    {item.label === "Workshops" && (
                                        <span className="early-bird-badge">
                                            Early Bird
                                        </span>
                                    )}
                                    {item.label}
                                </span>
                            </a>
                        </React.Fragment>
                    );
                })}
            </div>

            {isProfilePage ? (
                <div
                    className="sign-in-btn sign-out"
                    onClick={() => setShowLogoutModal(true)}
                >
                    <LogOut size={16} /> Sign Out
                </div>
            ) : (
                <div
                    className="sign-in-btn"
                    onClick={async () => {
                        if (tfUser || currentUser) {
                            navigate("/profile");
                        } else {
                            try {
                                if (!currentUser) await loginWithGoogle();
                                const res = await authFetch(
                                    "https://techfest.org/api/google-signin/",
                                    { method: "POST" },
                                );
                                if (res.ok) {
                                    const data = await res.json();
                                    setTfUser(data);
                                }
                            } catch (e) {
                                console.error("Login failed", e);
                            }
                        }
                    }}
                >
                    {tfUser ? (
                        <>
                            <img
                                src={`https://techfest.org/media/${tfUser.avatar}.png`}
                                alt="avatar"
                                style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                }}
                                onError={(e) => {
                                    e.target.style.display = "none";
                                }}
                            />
                            {tfUser.name
                                ? tfUser.name.split(" ")[0]
                                : "Profile"}
                        </>
                    ) : currentUser ? (
                        <>
                            {currentUser.displayName
                                ? currentUser.displayName.split(" ")[0]
                                : "Profile"}
                        </>
                    ) : (
                        <>
                            <LogIn size={16} /> Sign In
                        </>
                    )}
                </div>
            )}

            <a href="/ces" className="mobile-ces-btn" aria-label="CES">
                <CesIcon size={14} />
            </a>

            {showLogoutModal && (
                <div
                    className="logout-modal-backdrop"
                    onClick={() => setShowLogoutModal(false)}
                >
                    <div
                        className="logout-modal-card"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="logout-modal-icon">
                            <LogOut size={26} />
                        </div>
                        <h3 className="logout-modal-title">Sign Out</h3>
                        <p className="logout-modal-message">
                            Are you sure you want to sign out?
                        </p>
                        <div className="logout-modal-actions">
                            <button
                                className="logout-modal-btn cancel"
                                onClick={() => setShowLogoutModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="logout-modal-btn confirm"
                                onClick={async () => {
                                    setShowLogoutModal(false);
                                    try {
                                        await logout();
                                        setTfUser(null);
                                        navigate("/");
                                    } catch (err) {
                                        console.error("Logout failed", err);
                                    }
                                }}
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="sidebar left">
                <a href="/" className="sidebar-item">
                    <Home size={26} />
                    <span>Home</span>
                </a>
                <div
                    className={`sidebar-item ${activeMenu === "events" ? "active" : ""}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleMenu("events");
                    }}
                >
                    <Calendar size={26} />
                    <span>Events</span>
                    <PopoutBox
                        isOpen={activeMenu === "events"}
                        inlineItems={eventsItems}
                        onClose={() => setActiveMenu(null)}
                        gsapLoaded={gsapLoaded}
                    />
                </div>
                <a href="/ces" className="sidebar-item">
                    <CesIcon size={20} />
                    <span>CES</span>
                </a>
                <a href="/contactus" className="sidebar-item">
                    <Phone size={26} />
                    <span>Contact Us</span>
                </a>
                <div
                    className={`sidebar-item ${activeMenu === "about" ? "active" : ""}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleMenu("about");
                    }}
                >
                    <Users size={26} />
                    <span>About Us</span>
                    <PopoutBox
                        isOpen={activeMenu === "about"}
                        inlineItems={aboutItems}
                        onClose={() => setActiveMenu(null)}
                        gsapLoaded={gsapLoaded}
                    />
                </div>
            </div>

            <div className="sidebar right">
                {socialLinks.map(({ Icon, href, label, color }) => (
                    <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sidebar-item"
                        aria-label={label}
                        style={{ "--brand": color }}
                    >
                        <Icon size={26} />
                    </a>
                ))}
            </div>

            <div className="bottom-nav">
                {bottomNavItems.map((item) => {
                    const { label, Icon } = item;
                    if (item.type === "popout") {
                        return (
                            <div
                                key={label}
                                className={`bottom-nav-item ${activeMenu === "mobileEvents" ? "active" : ""}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMenu("mobileEvents");
                                }}
                            >
                                <Icon size={20} />
                                <span>{label}</span>
                                <PopoutBox
                                    isOpen={activeMenu === "mobileEvents"}
                                    inlineItems={item.items}
                                    onClose={() => setActiveMenu(null)}
                                    gsapLoaded={gsapLoaded}
                                    positionClass="bottom-popout"
                                />
                            </div>
                        );
                    }
                    return (
                        <a
                            key={label}
                            href={item.href}
                            className="bottom-nav-item"
                        >
                            <Icon size={20} />
                            <span>{label}</span>
                        </a>
                    );
                })}
            </div>
        </div>
    );
}
